import { ConfirmDialog } from '@components/ConfirmDialog/ConfirmDialog';
import { LoadingActionButton } from '@components/LoadingActionButton/LoadingActionButton';
import {
  ActionDrawerItem,
  MobileActionDrawer,
} from '@components/MobileActionDrawer/MobileActionDrawer';
import { Toast } from '@components/Toast/Toast';
import { useIsMobile } from '@lib/hooks/sizing/useIsMobile';
import dialogCss from '@lib/style/dialog.module.scss';
import { formatDate, pluralize } from '@lib/utils/units';
import {
  AddRounded,
  ContentCopyRounded,
  DeleteOutlineRounded,
  InfoOutlined,
  MoreHorizRounded,
  RefreshRounded,
  VpnKeyRounded,
  WarningAmberRounded,
} from '@mui/icons-material';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Popover,
  TextField,
  Tooltip,
} from '@mui/material';
import { ActionButton } from '@pages/ActionButton';
import {
  apiKeysStore,
  ApiScope,
  HIDDEN_SCOPES,
  SanitizedApiKey,
  SCOPE_DESCRIPTIONS,
  SCOPE_GROUPS,
  SCOPE_LABELS,
} from '@stores/ApiKeys';
import { trackingStore } from '@stores/Tracking';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import css from './ApiKeys.module.scss';

// ── Helpers ────────────────────────────────────────────────────

async function copyToClipboard(text: string, successMessage: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast.custom((t) => <Toast id={t} header={successMessage} variant="success" />, {
      duration: 2000,
    });
  } catch {
    toast.custom((t) => <Toast id={t} header="Failed to copy to clipboard" variant="error" />, {
      duration: 3000,
    });
  }
}

// ── Scope Picker ───────────────────────────────────────────────

function ScopePicker({
  selected,
  onChange,
}: {
  selected: ApiScope[];
  onChange: (scopes: ApiScope[]) => void;
}) {
  const toggle = (scope: ApiScope) => {
    if (selected.includes(scope)) {
      onChange(selected.filter((s) => s !== scope));
    } else {
      onChange([...selected, scope]);
    }
  };

  return (
    <div className={css.scopePickerSection}>
      {SCOPE_GROUPS.map((group) => (
        <div key={group.label} className={css.scopeGroup}>
          <div className={css.scopeGroupLabel}>{group.label}</div>
          <div className={css.scopeOptions}>
            {group.scopes
              .filter((scope) => !HIDDEN_SCOPES.has(scope))
              .map((scope) => (
                <Tooltip key={scope} title={SCOPE_DESCRIPTIONS[scope]} placement="top" arrow>
                  <button
                    type="button"
                    className={classNames(css.scopeChipToggle, {
                      [css.active]: selected.includes(scope),
                    })}
                    onClick={() => toggle(scope)}
                  >
                    {SCOPE_LABELS[scope]}
                  </button>
                </Tooltip>
              ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Create Key Dialog ──────────────────────────────────────────

function CreateKeyDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<ApiScope[]>(['profile:read']);
  const [loading, setLoading] = useState(false);
  const [revealedKey, setRevealedKey] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName('');
      setScopes(['profile:read']);
      setRevealedKey(null);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!name.trim()) return;
    if (scopes.length === 0) return;

    setLoading(true);
    try {
      const result = await apiKeysStore.createKey(name.trim(), scopes);
      trackingStore.apiKeyCreated({ name: name.trim(), scopeCount: scopes.length });
      setRevealedKey(result.key);
      toast.custom((t) => <Toast id={t} header="API key created" variant="success" />, {
        duration: 3000,
      });
    } catch (e) {
      toast.custom(
        (t) => (
          <Toast
            id={t}
            header={e instanceof Error ? e.message : 'Failed to create API key'}
            variant="error"
          />
        ),
        { duration: 4000 },
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    if (!revealedKey) {
      trackingStore.apiKeyCreateDialogCancelled();
    }
    onClose();
  };

  if (revealedKey) {
    return (
      <Dialog open={open} onClose={handleClose} className={dialogCss.dialog}>
        <DialogTitle className={dialogCss.dialogTitle}>API Key Created</DialogTitle>
        <DialogContent className={dialogCss.dialogContent}>
          <div className={css.revealContainer}>
            <div className={css.warningBanner}>
              <WarningAmberRounded />
              <span>Make sure to copy your API key now. You won't be able to see it again.</span>
            </div>
            <div className={css.revealKeyBox}>
              <span className={css.revealKeyText}>{revealedKey}</span>
              <IconButton
                onClick={() => {
                  trackingStore.apiKeyCopied({ context: 'create' });
                  copyToClipboard(revealedKey, 'API key copied to clipboard');
                }}
                size="small"
                className={css.revealCopyButton}
              >
                <ContentCopyRounded
                  sx={{ width: 16, height: 16, color: 'rgba(255,255,255,0.5)' }}
                />
              </IconButton>
            </div>
          </div>
        </DialogContent>
        <DialogActions className={dialogCss.dialogActions}>
          <ActionButton onClick={handleClose}>Done</ActionButton>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} className={dialogCss.dialog}>
      <DialogTitle className={dialogCss.dialogTitle}>Create API Key</DialogTitle>
      <DialogContent className={dialogCss.dialogContent}>
        <div>
          <div className={css.fieldLabel}>Name</div>
          <TextField
            autoFocus
            placeholder="e.g. Production Key"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={css.nameInput}
            size="small"
          />
        </div>
        <div>
          <div className={css.fieldLabel}>Scopes</div>
          <ScopePicker selected={scopes} onChange={setScopes} />
        </div>
      </DialogContent>
      <DialogActions className={dialogCss.dialogActions}>
        <ActionButton variant="secondary" onClick={handleClose}>
          Cancel
        </ActionButton>
        <LoadingActionButton
          onClick={handleSubmit}
          disabled={!name.trim() || scopes.length === 0}
          loadingText="Creating..."
        >
          Create Key
        </LoadingActionButton>
      </DialogActions>
    </Dialog>
  );
}

// ── Key Row Actions ─────────────────────────────────────────────

type KeyAction = 'copy-key-id' | 'rotate' | 'delete';

const KEY_ACTIONS: (ActionDrawerItem & { id: KeyAction })[] = [
  { id: 'copy-key-id', label: 'Copy Key ID', icon: <ContentCopyRounded /> },
  { id: 'rotate', label: 'Rotate', icon: <RefreshRounded /> },
  { id: 'delete', label: 'Delete', icon: <DeleteOutlineRounded />, danger: true },
];

// ── Key Row ────────────────────────────────────────────────────

function KeyRow({ apiKey }: { apiKey: SanitizedApiKey }) {
  const isMobile = useIsMobile();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scopesAnchor, setScopesAnchor] = useState<null | HTMLElement>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [rotateOpen, setRotateOpen] = useState(false);
  const [rotateRevealedKey, setRotateRevealedKey] = useState<string | null>(null);

  const closeMenu = () => {
    setMenuAnchor(null);
    setDrawerOpen(false);
  };

  const handleAction = (action: KeyAction) => {
    closeMenu();
    switch (action) {
      case 'copy-key-id':
        trackingStore.apiKeyCopied({ context: 'key-id' });
        copyToClipboard(apiKey._id, 'Key ID copied to clipboard');
        break;
      case 'rotate':
        setRotateOpen(true);
        break;
      case 'delete':
        setDeleteOpen(true);
        break;
    }
  };

  const handleDelete = async () => {
    try {
      await apiKeysStore.deleteKey(apiKey._id);
      trackingStore.apiKeyDeleted({ keyId: apiKey._id });
      setDeleteOpen(false);
      toast.custom((t) => <Toast id={t} header="API key deleted" variant="success" />, {
        duration: 3000,
      });
    } catch (e) {
      toast.custom(
        (t) => (
          <Toast
            id={t}
            header={e instanceof Error ? e.message : 'Failed to delete API key'}
            variant="error"
          />
        ),
        { duration: 4000 },
      );
    }
  };

  const handleRotate = async () => {
    try {
      const result = await apiKeysStore.rotateKey(apiKey._id);
      trackingStore.apiKeyRotated({ keyId: apiKey._id });
      setRotateOpen(false);
      setRotateRevealedKey(result.key);
    } catch (e) {
      toast.custom(
        (t) => (
          <Toast
            id={t}
            header={e instanceof Error ? e.message : 'Failed to rotate API key'}
            variant="error"
          />
        ),
        { duration: 4000 },
      );
    }
  };

  const openMenu = (e: React.MouseEvent<HTMLElement>) => {
    if (isMobile) {
      setDrawerOpen(true);
    } else {
      setMenuAnchor(e.currentTarget);
    }
  };

  return (
    <>
      <div className={css.keyRow}>
        <div className={css.keyName}>
          <span className={css.name}>{apiKey.name}</span>
          <span className={css.scopesBadge} onClick={(e) => setScopesAnchor(e.currentTarget)}>
            <InfoOutlined />
          </span>
        </div>
        <div className={css.keyValue}>
          <span className={css.keyText}>{apiKey.key}</span>
        </div>
        <div className={css.keyDate}>{formatDate(apiKey.createdAt)}</div>
        <div className={css.keyEnabled}>
          <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>
            {pluralize(apiKey.scopes.length, 'scope')}
          </span>
        </div>
        <div className={css.keyActions}>
          <button className={css.menuButton} onClick={openMenu}>
            <MoreHorizRounded />
          </button>
        </div>
      </div>

      {/* Scopes Popover */}
      <Popover
        open={Boolean(scopesAnchor)}
        anchorEl={scopesAnchor}
        onClose={() => setScopesAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              background: '#1e1e1e',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
            },
          },
        }}
      >
        <div className={css.scopesPopover}>
          <div className={css.scopesTitle}>Scopes</div>
          <div className={css.scopesList}>
            {apiKey.scopes.map((scope) => (
              <span key={scope} className={css.scopeChip}>
                {scope}
              </span>
            ))}
          </div>
        </div>
      </Popover>

      {/* Desktop: Context Menu */}
      {!isMobile && (
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          className={css.contextMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {KEY_ACTIONS.map((action) => (
            <MenuItem
              key={action.id}
              onClick={() => handleAction(action.id)}
              className={action.danger ? css.dangerItem : undefined}
            >
              {action.icon} {action.label}
            </MenuItem>
          ))}
        </Menu>
      )}

      {/* Mobile: Drawer */}
      {isMobile && (
        <MobileActionDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          onOpen={() => setDrawerOpen(true)}
          title={apiKey.name}
          actions={KEY_ACTIONS}
          onAction={handleAction}
        />
      )}

      {/* Delete Confirm */}
      <ConfirmDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
        title="Delete API Key"
        confirmLabel="Delete"
        loadingText="Deleting..."
      >
        Are you sure you want to delete <strong>{apiKey.name}</strong>? This action cannot be
        undone. The key will stop working within 60 seconds.
      </ConfirmDialog>

      {/* Rotate Confirm */}
      <ConfirmDialog
        open={rotateOpen}
        onClose={() => setRotateOpen(false)}
        onConfirm={handleRotate}
        title="Rotate API Key"
        confirmLabel="Rotate"
        confirmVariant="primary"
        loadingText="Rotating..."
      >
        This will generate a new key for <strong>{apiKey.name}</strong> and immediately invalidate
        the current one. Any applications using the old key will stop working within 60 seconds.
      </ConfirmDialog>

      {/* Rotated Key Reveal */}
      <Dialog
        open={Boolean(rotateRevealedKey)}
        onClose={() => setRotateRevealedKey(null)}
        className={dialogCss.dialog}
      >
        <DialogTitle className={dialogCss.dialogTitle}>New API Key Generated</DialogTitle>
        <DialogContent className={dialogCss.dialogContent}>
          <div className={css.revealContainer}>
            <div className={css.warningBanner}>
              <WarningAmberRounded />
              <span>
                Make sure to copy your new API key now. You won't be able to see it again.
              </span>
            </div>
            <div className={css.revealKeyBox}>
              <span className={css.revealKeyText}>{rotateRevealedKey}</span>
              <IconButton
                onClick={() => {
                  trackingStore.apiKeyCopied({ context: 'rotate' });
                  copyToClipboard(rotateRevealedKey!, 'New API key copied to clipboard');
                }}
                size="small"
                className={css.revealCopyButton}
              >
                <ContentCopyRounded
                  sx={{ width: 16, height: 16, color: 'rgba(255,255,255,0.5)' }}
                />
              </IconButton>
            </div>
          </div>
        </DialogContent>
        <DialogActions className={dialogCss.dialogActions}>
          <ActionButton onClick={() => setRotateRevealedKey(null)}>Done</ActionButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ── Main Component ─────────────────────────────────────────────

export const ApiKeys = observer(function ApiKeys() {
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    apiKeysStore.fetchKeys();
  }, []);

  return (
    <div className={css.container}>
      <div className={css.header}>
        <div className={css.headerLeft}>
          <div className={css.title}>API Keys</div>
          <div className={css.subtitle}>
            Create and manage API keys to access the API programmatically. Keys are scoped to
            specific permissions.
          </div>
        </div>
        <ActionButton
          onClick={() => {
            trackingStore.apiKeyCreateDialogOpened();
            setCreateOpen(true);
          }}
        >
          <AddRounded sx={{ width: 18, height: 18, marginRight: '4px' }} />
          Create Key
        </ActionButton>
      </div>

      {apiKeysStore.fetching && apiKeysStore.keys.length === 0 ? (
        <div className={css.loading}>Loading API keys...</div>
      ) : apiKeysStore.error ? (
        <div className={css.errorState}>
          <span>Failed to load API keys.</span>
          <ActionButton variant="secondary" onClick={() => apiKeysStore.fetchKeys()}>
            Retry
          </ActionButton>
        </div>
      ) : apiKeysStore.keys.length === 0 ? (
        <div className={css.emptyState}>
          <div className={css.emptyIcon}>
            <VpnKeyRounded sx={{ width: 48, height: 48 }} />
          </div>
          <div className={css.emptyTitle}>No API keys yet</div>
          <div className={css.emptySubtitle}>
            Create an API key to start using the API. Each key can be scoped to specific
            permissions.
          </div>
          <ActionButton
            onClick={() => {
              trackingStore.apiKeyCreateDialogOpened();
              setCreateOpen(true);
            }}
          >
            <AddRounded sx={{ width: 18, height: 18, marginRight: '4px' }} />
            Create Key
          </ActionButton>
        </div>
      ) : (
        <>
          <div className={css.tableHeader}>
            <span>Name</span>
            <span>Key</span>
            <span>Created</span>
            <span>Scopes</span>
            <span />
          </div>
          <div className={css.divider} />
          {apiKeysStore.sortedKeys.map((key) => (
            <KeyRow key={key._id} apiKey={key} />
          ))}
          <div className={css.footerNote}>
            API keys are scoped to your user account and use the billing account associated with
            your profile.
          </div>
        </>
      )}

      <CreateKeyDialog open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  );
});
